import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  canNest,
  DEFAULT_NOTE_FOLDER_COLOR,
  NOTE_FOLDER_COLORS,
  NOTE_LIMITS,
  type Note,
  type NoteFolder,
} from '@td/shared';
import {
  useCreateFolder,
  useCreateNote,
  useDeleteFolder,
  useDeleteNote,
  useEmptyTrash,
  useNote,
  useNoteCounts,
  useNoteFolders,
  useNotes,
  useRestoreNote,
  useUpdateFolder,
  useUpdateNote,
} from '../hooks/useNotes';
import { NoteEditor } from '../components/NoteEditor';
import { useConfirm } from '../components/ConfirmDialog';
import { Doodle } from '../components/Doodle';
import { Portal } from '../components/Portal';
import { zonedNow } from '../lib/dates';

/** The one icon that means "throw this away", wherever it appears. */
function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 7h16M10 4h4a1 1 0 0 1 1 1v2H9V5a1 1 0 0 1 1-1zM6 7l1 13a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-13M10 11v6M14 11v6" />
    </svg>
  );
}

/** "Today at 4:32 PM", "Yesterday at …", or a date once it is older. */
function stamp(iso: string): string {
  const then = new Date(iso);
  const now = zonedNow();

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  const time = then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  if (sameDay(then, now)) return `Today at ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(then, yesterday)) return `Yesterday at ${time}`;

  return `${then.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: then.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  })} at ${time}`;
}

/**
 * Dragging a note onto a folder.
 *
 * Pointer events rather than the HTML5 drag API. That API drags a translucent
 * ghost of the element rather than the element, gives no usable coordinates
 * during the drag in some browsers, and does not fire at all on a touchscreen
 * — and this dashboard is used on a classroom tablet as well as a laptop.
 *
 * Drop targets register their own element rather than being found by
 * `elementFromPoint`, because the note being dragged sits under the pointer
 * the whole time and would be the thing found.
 */
/**
 * What is in the air: a note being filed, or a folder being moved.
 *
 * One gesture rather than two. A teacher who has learnt that notes are moved
 * by dragging them will try it on a folder, and a sidebar where that does
 * nothing looks broken rather than restrained.
 */
type Dragged = { kind: 'note'; note: Note } | { kind: 'folder'; folder: NoteFolder };

interface NoteDrag {
  /** What is under the pointer, once the gesture has passed the threshold. */
  item: Dragged | null;
  at: { x: number; y: number };
  /** The row it is currently over: a folder id, 'unfiled', 'trash', or 'top'. */
  over: string | null;
  begin: (item: Dragged, e: React.PointerEvent) => void;
  registerTarget: (id: string, el: HTMLElement | null) => void;
  /** True while a drag is in flight, so a click can be swallowed. */
  moved: React.MutableRefObject<boolean>;
}

function useNoteDrag(onDrop: (item: Dragged, targetId: string) => void): NoteDrag {
  const [note, setNote] = useState<Dragged | null>(null);
  const [at, setAt] = useState({ x: 0, y: 0 });
  const [over, setOver] = useState<string | null>(null);

  const targets = useRef(new Map<string, HTMLElement>());
  const start = useRef<{
    x: number;
    y: number;
    note: Dragged;
    /** Kept so the pointer can be captured *later* — see `begin`. */
    el: HTMLElement;
    pointerId: number;
  } | null>(null);
  const moved = useRef(false);

  const registerTarget = useCallback((id: string, el: HTMLElement | null) => {
    if (el) targets.current.set(id, el);
    else targets.current.delete(id);
  }, []);

  /** Which target the pointer is inside, by rectangle. */
  const targetAt = useCallback((x: number, y: number) => {
    for (const [id, el] of targets.current) {
      const box = el.getBoundingClientRect();
      if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) return id;
    }
    return null;
  }, []);

  const begin = useCallback((n: Dragged, e: React.PointerEvent) => {
    // Only the primary button, and never a right-click.
    if (e.button !== 0) return;

    /*
     * A press on a control inside the row is not a drag on the row. The
     * folder rows carry a chevron, a rename and a trash can, and a press that
     * lands on one of those must stay a press — this is the exact shape that
     * made the ✕ on the jobs board look broken.
     */
    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;

    start.current = {
      x: e.clientX,
      y: e.clientY,
      note: n,
      el: e.currentTarget as HTMLElement,
      pointerId: e.pointerId,
    };
    moved.current = false;

    /*
     * The pointer is deliberately *not* captured here.
     *
     * Capturing on pointerdown hands the whole gesture to the capturing
     * element, including the click at the end of it — so a folder row that
     * captured immediately would swallow the clicks belonging to the buttons
     * sitting inside it. Capture happens once the pointer has travelled far
     * enough to be a drag, by which point there is no click left to steal.
     */
  }, []);

  /*
   * Dragging across a page of text selects it, which leaves the note looking
   * highlighted after the drop and swallows the next click.
   */
  useEffect(() => {
    if (!note) return;
    document.body.classList.add('select-none');
    return () => document.body.classList.remove('select-none');
  }, [note]);

  useEffect(() => {
    function move(e: PointerEvent) {
      const from = start.current;
      if (!from) return;

      // A threshold, so opening a note is still a click. Without it every
      // press that wobbles a pixel becomes a drag.
      if (!moved.current && Math.hypot(e.clientX - from.x, e.clientY - from.y) < 6) return;

      // Now it is a drag, so take the pointer — see `begin` for why not sooner.
      if (!moved.current) {
        try {
          from.el.setPointerCapture(from.pointerId);
        } catch {
          // The row can be gone if a live update re-rendered mid-gesture. The
          // window listeners carry on regardless.
        }
      }

      moved.current = true;
      setNote(from.note);
      setAt({ x: e.clientX, y: e.clientY });
      setOver(targetAt(e.clientX, e.clientY));
    }

    function up(e: PointerEvent) {
      const from = start.current;
      start.current = null;

      if (from && moved.current) {
        const target = targetAt(e.clientX, e.clientY);
        // What each row means is decided by the page, which is the only thing
        // that knows the tree and therefore whether a drop is legal at all.
        if (target) onDrop(from.note, target);
      }

      setNote(null);
      setOver(null);
    }

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);

    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [onDrop, targetAt]);

  return { item: note, at, over, begin, registerTarget, moved };
}

export function NotesPage() {
  /*
   * Opens on Unfiled rather than on an "Everything" row.
   *
   * There used to be both, and Trevor was right that they read as the same
   * thing: two rows at the top of the list, one of which claimed to hold the
   * other. The useful half of Everything was searching across the lot, and
   * that is now what searching does — see `scope` below — so the row itself
   * had nothing left to offer.
   */
  const [folder, setFolder] = useState<string>('unfiled');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  /*
   * A new note is written before it exists.
   *
   * It used to be created the instant the button was pressed and then left
   * sitting in the list, so pressing New note twice while thinking about it
   * left two empty notes behind, and the button did not appear to do anything
   * at all. Now it opens a blank page and Save is what creates it — which is
   * also the only version where changing your mind leaves nothing behind.
   */
  const [drafting, setDrafting] = useState(false);

  const { data: folders = [] } = useNoteFolders();
  const updateNote = useUpdateNote();
  const updateFolder = useUpdateFolder();
  const deleteNote = useDeleteNote();

  /*
   * A search looks everywhere.
   *
   * "Which note was it that mentioned the orchard?" is not a question about
   * the folder currently selected, and answering it from inside one folder is
   * how a search comes back empty for something that is plainly there. The
   * trash is the exception: thrown away means out of the way.
   */
  const scope = search.trim() ? '' : folder;
  const { data: notes = [], isLoading } = useNotes(scope, search);

  const tree = useMemo(
    () => folders.map((f) => ({ id: f.id, parentId: f.parentId })),
    [folders],
  );

  /**
   * What a row would do with what is being dragged, or null for nothing.
   *
   * Asked twice — once to light the row up and once when the pointer is let
   * go — so that what the sidebar promises under the cursor is exactly what
   * happens, rather than two nearly-identical conditions drifting apart.
   */
  const allows = useCallback(
    (item: Dragged, targetId: string): boolean => {
      if (item.kind === 'note') {
        // A note goes into any folder, out of all of them, or away. The
        // top-level strip is about folders and means nothing to a note.
        if (targetId === 'top') return false;
        if (targetId === 'unfiled') return item.note.folderId !== null;
        if (targetId === 'trash') return true;
        return item.note.folderId !== targetId;
      }

      if (targetId === 'top') return item.folder.parentId !== null;
      if (targetId === 'unfiled' || targetId === 'trash') return false;
      if (item.folder.parentId === targetId) return false;

      return canNest(tree, item.folder.id, targetId).ok;
    },
    [tree],
  );

  const onDrop = useCallback(
    (item: Dragged, targetId: string) => {
      if (!allows(item, targetId)) return;

      if (item.kind === 'note') {
        if (targetId === 'trash') deleteNote.mutate(item.note.id);
        else updateNote.mutate({ id: item.note.id, folderId: targetId === 'unfiled' ? null : targetId });
        return;
      }

      updateFolder.mutate({ id: item.folder.id, parentId: targetId === 'top' ? null : targetId });
    },
    [allows, deleteNote, updateFolder, updateNote],
  );

  const drag = useNoteDrag(onDrop);

  // A note that vanishes from the list — deleted, or filtered out by a search
  // — shouldn't leave an editor open on nothing.
  useEffect(() => {
    if (selected && !notes.some((n) => n.id === selected)) setSelected(null);
  }, [notes, selected]);

  return (
    <div className="mx-auto max-w-6xl">
      <header className="page-header flex flex-wrap items-end justify-between gap-3">
        <h1>Notes</h1>

        <button
          onClick={() => {
            setSelected(null);
            setDrafting(true);
          }}
          className="btn-primary"
        >
          New note
        </button>
      </header>

      <div className="flex flex-col gap-4 lg:flex-row">
        <Folders
          selected={folder}
          onSelect={(id) => {
            setFolder(id);
            setSelected(null);
            setDrafting(false);
          }}
          drag={drag}
          allows={allows}
        />

        <div className="min-w-0 flex-1">
          {/* Nothing to search while a blank page is being written on. */}
          {!drafting && (
            <input
              className="field mb-3"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search titles and what's inside…"
              aria-label="Search notes"
            />
          )}

          {drafting ? (
            <NoteDraft
              folderId={folder && folder !== 'unfiled' ? folder : null}
              onCancel={() => setDrafting(false)}
              onSaved={(id) => {
                setDrafting(false);
                setSelected(id);
              }}
            />
          ) : selected ? (
            <NoteView id={selected} onClose={() => setSelected(null)} />
          ) : (
            <NoteList
              notes={notes}
              loading={isLoading}
              search={search}
              searchedEverywhere={scope === '' && folder !== ''}
              inTrash={folder === 'trash' && !search.trim()}
              onOpen={setSelected}
              drag={drag}
            />
          )}
        </div>
      </div>

      {/*
        The note itself, following the pointer. Portalled so it is never
        clipped by the list's own scroll box, and transparent to the pointer
        so the folder underneath is the one being hit-tested.
      */}
      {drag.item && (
        <Portal>
          <div
            className="pointer-events-none fixed z-[95] max-w-xs truncate rounded-lg border border-terracotta bg-cream px-3 py-2 text-sm text-espresso shadow-lifted"
            style={{ left: drag.at.x + 12, top: drag.at.y + 12 }}
          >
            {drag.item.kind === 'note'
              ? drag.item.note.title || 'Untitled'
              : drag.item.folder.name}
          </div>
        </Portal>
      )}
    </div>
  );
}

/** Which folders are folded up, remembered on this device. */
const COLLAPSED_KEY = 'td.notes.collapsed.v1';

/* ---------------------------------------------------------------- folders */

function Folders({
  selected,
  onSelect,
  drag,
  allows,
}: {
  selected: string;
  onSelect: (id: string) => void;
  drag: NoteDrag;
  /** Whether a row would take what is in the air. Owned by the page. */
  allows: (item: Dragged, targetId: string) => boolean;
}) {
  const { data: folders = [] } = useNoteFolders();
  const { data: counts } = useNoteCounts();
  const createFolder = useCreateFolder();
  const updateFolder = useUpdateFolder();
  const deleteFolder = useDeleteFolder();

  /*
   * `adding` is null when the form is closed, and otherwise holds the parent
   * the new folder will go under — '' for the top level. One piece of state
   * rather than a boolean and a parent that can disagree with it.
   */
  /*
   * Which parents are folded up. Per device rather than per account: the
   * laptop and the classroom machine have different amounts of screen, and
   * folding a folder away is about the screen in front of you.
   */
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]') as unknown;
      return new Set(Array.isArray(saved) ? (saved as string[]) : []);
    } catch {
      return new Set();
    }
  });

  function toggleCollapsed(id: string) {
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      try {
        localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      } catch {
        // Not remembered; it still folds for this session.
      }
      return next;
    });
  }

  const [adding, setAdding] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_NOTE_FOLDER_COLOR);
  const [editing, setEditing] = useState<string | null>(null);

  const full = folders.length >= NOTE_LIMITS.maxFolders;

  /** Top-level folders, each with whatever is filed inside it. */
  const treeOf = useMemo(() => {
    const top = folders.filter((f) => !f.parentId);
    return top.map((parent) => ({
      folder: parent,
      children: folders.filter((f) => f.parentId === parent.id),
    }));
  }, [folders]);

  /*
   * A folder held over during a drag opens itself.
   *
   * Without this, filing a note into a subfolder means dropping the note,
   * unfolding the parent, and picking the note up again — and the reason it
   * is folded up is usually that it has a lot of subfolders, which is exactly
   * when this matters most.
   */
  const hovered = drag.item ? drag.over : null;

  useEffect(() => {
    if (!hovered || !collapsed.has(hovered)) return;

    const id = window.setTimeout(() => toggleCollapsed(hovered), 500);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovered, collapsed]);

  async function onAdd() {
    if (!name.trim() || adding === null) return;
    await createFolder.mutateAsync({
      name: name.trim(),
      color,
      parentId: adding || null,
    });
    setName('');
    setColor(DEFAULT_NOTE_FOLDER_COLOR);
    setAdding(null);
  }

  const addForm = (
    <div className="rounded-lg border border-sand p-2">
      <input
        className="field mb-2"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={adding ? 'Subfolder name' : 'Folder name'}
        aria-label="New folder name"
        maxLength={NOTE_LIMITS.folderName}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') void onAdd();
          if (e.key === 'Escape') setAdding(null);
        }}
      />
      <Swatches value={color} onPick={setColor} />
      <div className="mt-2 flex gap-1">
        <button className="btn-soft-accent flex-1" onClick={() => void onAdd()}>
          Add
        </button>
        <button className="btn-soft flex-1" onClick={() => setAdding(null)}>
          Cancel
        </button>
      </div>
    </div>
  );

  function renderFolder(f: (typeof folders)[number], depth: number, childCount = 0) {
    if (editing === f.id) {
      return (
        <li
          key={f.id}
          className={
            depth ? 'ml-4 rounded-lg border border-sand p-2' : 'rounded-lg border border-sand p-2'
          }
        >
          <input
            className="field mb-2"
            defaultValue={f.name}
            autoFocus
            maxLength={NOTE_LIMITS.folderName}
            aria-label="Folder name"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateFolder.mutate({ id: f.id, name: e.currentTarget.value.trim() });
                setEditing(null);
              }
              if (e.key === 'Escape') setEditing(null);
            }}
          />
          <Swatches value={f.color} onPick={(c) => updateFolder.mutate({ id: f.id, color: c })} />

          {/* Moving a folder in or out of another one, without dragging. */}
          <label className="mt-2 block">
            <span className="label">Inside</span>
            <select
              className="field"
              value={f.parentId ?? ''}
              onChange={(e) => updateFolder.mutate({ id: f.id, parentId: e.target.value || null })}
            >
              <option value="">Nothing — top level</option>
              {folders
                .filter((o) => o.id !== f.id && !o.parentId)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
          </label>

          <div className="mt-2 flex items-center gap-1">
            <button className="btn-soft flex-1" onClick={() => setEditing(null)}>
              Done
            </button>

            {/*
              A trash can, and no question attached to it.

              Which is only fair because nothing here is destroyed: the notes
              inside go to Trash and can be put straight back. The folder's
              own name and colour do go for good, and that is the whole of
              what a mis-click costs.
            */}
            <button
              className="rounded-lg border border-sand p-2 text-stone transition-colors hover:border-rust hover:bg-rust/10 hover:text-rust"
              aria-label={`Throw away the folder ${f.name}`}
              title="Throw it away — the notes go to Trash"
              onClick={() => {
                deleteFolder.mutate(f.id);
                setEditing(null);
                if (selected === f.id) onSelect('unfiled');
              }}
            >
              <TrashIcon />
            </button>
          </div>
        </li>
      );
    }

    return (
      <FolderRow
        key={f.id}
        folder={f}
        allows={allows}
        label={f.name}
        // A parent counts everything beneath it; on its own it would read as
        // empty while its subfolders held a year of minutes.
        count={depth === 0 ? (f.totalCount ?? f.noteCount ?? 0) : (f.noteCount ?? 0)}
        color={f.color}
        active={selected === f.id}
        indent={depth > 0}
        drag={drag}
        dropId={f.id}
        collapsed={childCount > 0 ? collapsed.has(f.id) : undefined}
        onToggleCollapsed={childCount > 0 ? () => toggleCollapsed(f.id) : undefined}
        childCount={childCount}
        onSelect={() => onSelect(f.id)}
        onEdit={() => setEditing(f.id)}
        onAddChild={
          depth === 0 && !full
            ? () => {
                setName('');
                setColor(f.color);
                setAdding(f.id);
              }
            : undefined
        }
      />
    );
  }

  return (
    /*
     * Wider than it was, because a folder called "Staff Meetings" was being
     * shown as "Staff Meeti…". A folder you cannot read the name of is a
     * folder you have to open to identify, which is the one thing the list
     * exists to save you from. The rows wrap now as well — two short lines
     * beat one clipped one.
     */
    <aside className="card w-full lg:w-72 lg:shrink-0">
      <h2 className="mb-2 text-base">Folders</h2>

      <ul className="mb-3 space-y-0.5">
        {treeOf.map(({ folder, children }) => (
          <li key={folder.id}>
            <ul className="space-y-0.5">
              {renderFolder(folder, 0, children.length)}
              {!collapsed.has(folder.id) && children.map((child) => renderFolder(child, 1, 0))}

              {/*
                The form appears under the folder it belongs to, so there is
                never a question of which one a new subfolder is going into.
              */}
              {adding === folder.id && <li className="pl-4">{addForm}</li>}
            </ul>
          </li>
        ))}

        {/*
          Appears only while a subfolder is in the air, and only for one that
          is actually inside something. A permanent "top level" row would be a
          third thing in the list that is not a folder, which is precisely the
          clutter Everything was removed for.
        */}
        {drag.item?.kind === 'folder' && drag.item.folder.parentId && (
          <li>
            <div
              ref={(el) => drag.registerTarget('top', el)}
              className={[
                'rounded-lg border-2 border-dashed px-2 py-3 text-center text-xs transition-colors',
                drag.over === 'top'
                  ? 'border-terracotta bg-terracotta/15 text-espresso'
                  : 'border-clay/60 text-stone',
              ].join(' ')}
            >
              Drop here to bring it out
            </div>
          </li>
        )}

        <FolderRow
          label="Unfiled"
          count={counts?.unfiled}
          color="#C4A287"
          active={selected === 'unfiled'}
          onSelect={() => onSelect('unfiled')}
          drag={drag}
          allows={allows}
          dropId="unfiled"
        />

        {/*
          Undeletable, and last. Everything thrown away lands here — notes
          deleted one at a time and the contents of folders deleted whole —
          and can be put back from it. It is the reason none of those actions
          stops to ask.
        */}
        <FolderRow
          label="Trash"
          count={counts?.trash}
          color="#9C9086"
          active={selected === 'trash'}
          onSelect={() => onSelect('trash')}
          drag={drag}
          allows={allows}
          dropId="trash"
        />
      </ul>

      {adding === '' ? (
        addForm
      ) : (
        <button
          className="btn-soft-accent w-full"
          disabled={full}
          onClick={() => {
            setName('');
            setColor(DEFAULT_NOTE_FOLDER_COLOR);
            setAdding('');
          }}
        >
          {full ? `That is all ${NOTE_LIMITS.maxFolders}` : 'New folder'}
        </button>
      )}
    </aside>
  );
}

function FolderRow({
  folder,
  label,
  count,
  color,
  active,
  indent = false,
  drag,
  allows,
  dropId,
  collapsed,
  onToggleCollapsed,
  childCount = 0,
  onSelect,
  onEdit,
  onAddChild,
}: {
  /** The folder itself, when this row is one — Unfiled and Trash are not. */
  folder?: NoteFolder;
  label: string;
  count?: number;
  color: string;
  active: boolean;
  /** A subfolder, stepped in under its parent. */
  indent?: boolean;
  drag?: NoteDrag;
  allows?: (item: Dragged, targetId: string) => boolean;
  /** What this row means as a drop target. */
  dropId?: string;
  /** Undefined when the folder has nothing inside it to fold away. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  childCount?: number;
  onSelect: () => void;
  onEdit?: () => void;
  /** Only top-level folders can take one, so only they offer the button. */
  onAddChild?: () => void;
}) {
  /*
   * Only rows that would really take it light up.
   *
   * Every folder used to lift while a note was in the air, including ones
   * the drop would do nothing with. With folders draggable too that would be
   * an outright lie — a subfolder cannot go inside another subfolder, and a
   * row that glows and then refuses is worse than one that never glowed.
   */
  const droppable = !!drag?.item && !!dropId && !!allows?.(drag.item, dropId);
  const over = droppable && drag?.over === dropId;

  return (
    <li className={`group flex items-start gap-1 ${indent ? 'pl-4' : ''}`}>
      {/*
        The chevron is its own button, not part of the row.
        
        Folding a folder away and looking inside it are different intentions,
        and a teacher who wants to see what is in 26/27 should not have to
        fold it up to find out.
      */}
      {onToggleCollapsed ? (
        <button
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={`${collapsed ? 'Show' : 'Hide'} the ${childCount} folders inside ${label}`}
          title={collapsed ? 'Show what is inside' : 'Fold it away'}
          data-no-drag
          className="mt-1.5 shrink-0 rounded p-0.5 text-stone transition-colors hover:bg-linen hover:text-espresso"
        >
          <svg
            viewBox="0 0 24 24"
            className={`h-3.5 w-3.5 transition-transform ${collapsed ? '' : 'rotate-90'}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </button>
      ) : (
        // Keeps every row's dot on the same vertical line, folder or not.
        <span aria-hidden className="w-[18px] shrink-0" />
      )}

      <button
        ref={(el) => drag?.registerTarget(dropId ?? '', el)}
        /*
         * A real folder can be picked up and moved; Unfiled and Trash cannot,
         * because they are not folders and have nowhere to go.
         */
        onPointerDown={folder ? (e) => drag?.begin({ kind: 'folder', folder }, e) : undefined}
        onClick={() => {
          // A drag that ended on this row must not also select it.
          if (drag?.moved.current) return;
          onSelect();
        }}
        aria-pressed={active}
        className={[
          'flex min-w-0 flex-1 items-start gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
          folder ? 'cursor-grab' : '',
          active ? 'bg-linen text-espresso' : 'text-walnut hover:bg-linen/60',
          // A row that would take the drop lifts slightly; the one under the
          // pointer is ringed. Rows that would refuse it do neither.
          over ? 'bg-terracotta/15 ring-2 ring-terracotta' : '',
          droppable && !over ? 'bg-linen/40' : '',
          drag?.item?.kind === 'folder' && drag.item.folder.id === folder?.id ? 'opacity-40' : '',
        ].join(' ')}
      >
        <span
          aria-hidden
          className="mt-[7px] h-2.5 w-2.5 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />

        {/*
          Wrapped, not clipped. "Staff Meetings" was being shown as "Staff
          Meeti…" — a name you cannot read is a folder you have to open to
          identify, which is the one job the list has.
        */}
        <span className="min-w-0 flex-1 break-words">{label}</span>

        {collapsed && childCount > 0 && (
          <span
            className="mt-0.5 shrink-0 rounded-full bg-linen px-1.5 text-[10px] text-stone"
            title={`${childCount} folders inside`}
          >
            {childCount}
          </span>
        )}
        {count !== undefined && <span className="mt-0.5 shrink-0 text-xs text-stone">{count}</span>}
      </button>

      {/* Hidden until wanted: a rename button beside every folder is clutter. */}
      {onAddChild && (
        <button
          data-no-drag
          onClick={onAddChild}
          aria-label={`New folder inside ${label}`}
          title={`New folder inside ${label}`}
          className="btn-soft mt-0.5 px-1.5 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
        >
          +
        </button>
      )}

      {onEdit && (
        <button
          data-no-drag
          onClick={onEdit}
          aria-label={`Edit ${label}`}
          title={`Edit ${label}`}
          className="btn-soft mt-0.5 px-1.5 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
        >
          ✎
        </button>
      )}
    </li>
  );
}

function Swatches({ value, onPick }: { value: string; onPick: (color: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1">
      {NOTE_FOLDER_COLORS.map((c) => (
        <button
          key={c.value}
          type="button"
          onClick={() => onPick(c.value)}
          aria-label={c.label}
          title={c.label}
          aria-pressed={value === c.value}
          className={[
            'h-5 w-5 rounded-full border-2 transition-transform',
            value === c.value ? 'border-espresso' : 'border-transparent hover:scale-110',
          ].join(' ')}
          style={{ backgroundColor: c.value }}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ list */

function NoteList({
  notes,
  loading,
  search,
  searchedEverywhere,
  inTrash,
  onOpen,
  drag,
}: {
  notes: Note[];
  loading: boolean;
  search: string;
  /** True when the search widened past the selected folder, so it can say so. */
  searchedEverywhere: boolean;
  inTrash: boolean;
  onOpen: (id: string) => void;
  drag: NoteDrag;
}) {
  const restore = useRestoreNote();
  const empty = useEmptyTrash();
  const confirm = useConfirm();

  if (loading) return <p className="text-sm text-stone">Loading…</p>;

  const trashTools = inTrash && notes.length > 0 && (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      <button className="btn-soft" onClick={() => restore.mutate('all')}>
        Put it all back
      </button>

      {/*
        The one action in Notes that really destroys something, so the one
        that asks. Everything else can be undone from here, which is what
        buys those actions the right to say nothing.
      */}
      <button
        className="btn-soft-danger"
        onClick={async () => {
          const ok = await confirm({
            title: `Empty the trash?`,
            body: `${notes.length} ${notes.length === 1 ? 'note' : 'notes'} and any pictures in them are removed for good. This cannot be undone.`,
            confirmLabel: 'Empty it',
            danger: true,
          });
          if (ok) empty.mutate();
        }}
      >
        Empty the trash
      </button>
    </div>
  );

  if (notes.length === 0) {
    return (
      <div className="card relative">
        <p className="text-sm leading-relaxed text-stone">
          {search
            ? `Nothing matched "${search}" in any folder.`
            : inTrash
              ? 'The trash is empty.'
              : 'No notes here yet. New note starts one.'}
        </p>
        <span className="pointer-events-none absolute bottom-3 right-3 text-clay opacity-[0.12]">
          <Doodle name="notebook" size={56} />
        </span>
      </div>
    );
  }

  return (
    <>
      {trashTools}

      {searchedEverywhere && (
        <p className="mb-2 text-xs text-stone">Looking in every folder, not just this one.</p>
      )}

      <ul className="space-y-2">
        {notes.map((note) => (
          <li key={note.id}>
            <div
              onPointerDown={inTrash ? undefined : (e) => drag.begin({ kind: 'note', note }, e)}
              className={[
                'card relative transition-colors hover:border-clay',
                inTrash ? '' : 'cursor-grab',
                drag.item?.kind === 'note' && drag.item.note.id === note.id ? 'opacity-40' : '',
              ].join(' ')}
            >
              <button
                onClick={() => {
                  // A drag that ended on this card would otherwise open it.
                  if (drag.moved.current) return;
                  onOpen(note.id);
                }}
                className="block w-full text-left"
              >
                <h2 className="min-w-0 truncate">{note.title || 'Untitled'}</h2>

                {note.preview && (
                  <p className="mt-1 line-clamp-2 text-sm text-walnut">{note.preview}</p>
                )}

                <p className="mt-2 text-xs text-stone">Edited {stamp(note.updatedAt)}</p>
              </button>

              {/*
                Putting one back is the whole reason the trash exists, so it
                is on the card rather than inside it. Nowhere else needs a
                button here — a live note is opened, not acted on from a list.
              */}
              {inTrash && (
                <button
                  data-no-drag
                  className="btn-soft absolute right-3 top-3"
                  onClick={() => restore.mutate(note.id)}
                >
                  Put it back
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/* ----------------------------------------------------------------- draft */

/**
 * The way back, above the title.
 *
 * It used to be a full-width secondary button sitting in a row with Edit,
 * Delete and the rest, which gave a way out of a note the same weight as
 * deleting one. Leaving is not a decision; it is the corner of the page you
 * reach for without looking.
 */
function BackArrow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="-ml-1 mb-1 inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-sm text-stone transition-colors hover:bg-linen hover:text-espresso"
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M19 12H5m0 0 6-6m-6 6 6 6" />
      </svg>
      <span className="text-xs">Notes</span>
    </button>
  );
}

/**
 * A note being written, before it is a note.
 *
 * Nothing is created until Save is pressed, which is the whole point: the
 * button used to make an empty note on the spot and leave it in the list, so
 * pressing it while deciding what to write left litter behind and there was
 * nothing on the screen to say anything had happened.
 *
 * It opens into whichever folder is selected, because that is plainly what
 * "new note" means while looking at a folder — and the folder is still a
 * field here, so it can be changed before saving rather than filed afterwards.
 */
function NoteDraft({
  folderId,
  onCancel,
  onSaved,
}: {
  folderId: string | null;
  onCancel: () => void;
  onSaved: (id: string) => void;
}) {
  const { data: folders = [] } = useNoteFolders();
  const create = useCreateNote();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [where, setWhere] = useState(folderId ?? '');
  const [error, setError] = useState<string | null>(null);

  const empty = !title.trim() && !body.replace(/<[^>]*>/g, '').trim();

  async function save() {
    setError(null);
    try {
      const note = await create.mutateAsync({
        title: title.trim(),
        body,
        folderId: where || null,
      });
      onSaved(note.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That note could not be saved');
    }
  }

  return (
    <div className="card">
      <BackArrow label="Back to all notes" onClick={onCancel} />

      <input
        className="mb-2 w-full border-none bg-transparent font-display text-xl text-espresso placeholder:text-stone focus:outline-none"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
        aria-label="Note title"
        maxLength={NOTE_LIMITS.title}
        autoFocus
      />

      <div className="mb-3">
        <select
          className="field max-w-[12rem] text-xs"
          value={where}
          onChange={(e) => setWhere(e.target.value)}
          aria-label="Folder"
        >
          <option value="">Unfiled</option>
          {folders.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      <NoteEditor value={body} onChange={setBody} />

      {error && <p className="mt-2 text-sm text-rust">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          className="btn-primary"
          onClick={() => void save()}
          disabled={create.isPending || empty}
        >
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>

        {/*
          Said rather than enforced. A note with a title and no body is a
          perfectly ordinary thing to save, and so is the other way round — the
          only thing worth pointing out is that saving nothing at all is what
          the old button did by accident.
        */}
        {empty && <span className="self-center text-xs text-stone">Nothing written yet</span>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ note */

/**
 * A note, read first and edited on purpose.
 *
 * Opening straight into an editor meant a stray click could change a note
 * nobody meant to touch — and with a one-and-a-half second autosave, change
 * it permanently before anyone noticed. Reading is the common case; editing
 * is the deliberate one, so it takes a button.
 */
function NoteView({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: note, isLoading } = useNote(id);
  const { data: folders = [] } = useNoteFolders();
  const update = useUpdateNote();
  const remove = useDeleteNote();
  const restore = useRestoreNote();

  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the note once. Later re-renders must not overwrite what is being
  // typed, so this deliberately keys on the id and not on the note object.
  useEffect(() => {
    if (!note) return;
    setTitle(note.title);
    setBody(note.body);
    setDirty(false);
    setSavedAt(null);
    setEditing(false);
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Saves a second and a half after typing stops.
   *
   * Notes are long-form; asking someone to remember a Save button while
   * writing up a parent meeting is how work gets lost. The button is still
   * there for people who want to press something.
   */
  useEffect(() => {
    if (!dirty || !note || !editing) return;

    timer.current = setTimeout(() => {
      update.mutate(
        { id, title, body },
        {
          onSuccess: (saved) => {
            setDirty(false);
            setSavedAt(saved.updatedAt);
          },
        },
      );
    }, 1500);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [dirty, title, body, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  function save(then?: () => void) {
    if (timer.current) clearTimeout(timer.current);
    update.mutate(
      { id, title, body },
      {
        onSuccess: (saved) => {
          setDirty(false);
          setSavedAt(saved.updatedAt);
          then?.();
        },
      },
    );
  }

  async function leaveEditing() {
    // Nothing is lost silently: unsaved work is saved on the way out rather
    // than discarded, which is what autosave has already promised.
    if (dirty) save(() => setEditing(false));
    else setEditing(false);
  }

  if (isLoading || !note) return <p className="text-sm text-stone">Loading…</p>;

  return (
    <div className="card">
      <BackArrow label="Back to all notes" onClick={onClose} />

      {editing ? (
        <input
          className="mb-1 w-full border-none bg-transparent font-display text-xl text-espresso placeholder:text-stone focus:outline-none"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            setDirty(true);
          }}
          placeholder="Untitled"
          aria-label="Note title"
          maxLength={NOTE_LIMITS.title}
        />
      ) : (
        <h2 className="mb-1 break-words">{note.title || 'Untitled'}</h2>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone">
        <span>Created {stamp(note.createdAt)}</span>
        <span>·</span>
        <span>Edited {stamp(savedAt ?? note.updatedAt)}</span>
        {editing && (
          <>
            <span>·</span>
            <span className={dirty ? 'text-ochre' : 'text-olive'}>
              {dirty ? 'Unsaved changes' : 'Saved'}
            </span>
          </>
        )}
        {note.folderId && (
          <>
            <span>·</span>
            <span>{folders.find((f) => f.id === note.folderId)?.name ?? 'Unfiled'}</span>
          </>
        )}
      </div>

      {editing && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <select
            className="field max-w-[12rem] text-xs"
            value={note.folderId ?? ''}
            onChange={(e) => update.mutate({ id, folderId: e.target.value || null })}
            aria-label="Folder"
          >
            <option value="">Unfiled</option>
            {folders.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {editing ? (
        <NoteEditor
          value={body}
          noteId={id}
          onChange={(html) => {
            setBody(html);
            setDirty(true);
          }}
        />
      ) : (
        /*
          The same styling the editor uses, so a note does not shift about
          when it is opened for editing — and dangerouslySetInnerHTML on
          markup the server has already sanitised on every write.
        */
        <div
          className="note-body min-h-[8rem] text-sm leading-relaxed text-espresso"
          dangerouslySetInnerHTML={{ __html: note.body }}
        />
      )}

      {!editing && !note.body && (
        <p className="text-sm text-stone">This one is empty. Edit adds something to it.</p>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {editing ? (
          <>
            <button className="btn-primary" onClick={() => save()} disabled={!dirty}>
              {dirty ? 'Save' : 'Saved'}
            </button>
            <button className="btn-secondary" onClick={() => void leaveEditing()}>
              Done editing
            </button>
          </>
        ) : (
          <button className="btn-primary" onClick={() => setEditing(true)}>
            Edit
          </button>
        )}

        {note.deletedAt ? (
          <button
            className="btn-secondary ml-auto"
            onClick={() => {
              restore.mutate(id);
              onClose();
            }}
          >
            Put it back
          </button>
        ) : (
          /*
            A trash can, and nothing to confirm.

            The dialog that used to be here was doing real work — the note was
            being destroyed — and now it is not. It goes to Trash and can be
            put back, so stopping to ask would be asking about nothing.
          */
          <button
            className="ml-auto rounded-lg border border-sand p-2 text-stone transition-colors hover:border-rust hover:bg-rust/10 hover:text-rust"
            aria-label="Throw this note away"
            title="Throw it away — it goes to Trash"
            onClick={() => {
              remove.mutate(id);
              onClose();
            }}
          >
            <TrashIcon />
          </button>
        )}
      </div>
    </div>
  );
}
